import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Download } from 'lucide-react'
import type { SlotGeneration } from '@/lib/api/amazon-listing.types'
import { downloadSlotGeneration, triggerBlobDownload } from '@/lib/api/amazon-listing'
import { SlotGenerationThumbnail } from './SlotGenerationThumbnail'
import { SlotGenerationLightbox } from './SlotGenerationLightbox'

interface SlotGenerationGridProps {
  slotId: string
  generations: SlotGeneration[]
  selectedId: string | null
  generationCount: number
  isGenerating: boolean
  onSelect: (id: string) => void
  onDelete?: (id: string) => void
}

export function SlotGenerationGrid({
  slotId,
  generations,
  selectedId,
  generationCount,
  isGenerating,
  onSelect,
  onDelete,
}: SlotGenerationGridProps) {
  const { t } = useTranslation()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const filledCount = generations.length
  const emptyCount = Math.max(0, generationCount - filledCount)

  const handleDownloadAll = async () => {
    for (const gen of generations) {
      try {
        const blob = await downloadSlotGeneration(slotId, gen.id)
        triggerBlobDownload(blob, `generation-${gen.id}.png`)
      } catch { /* toast handled per-item */ }
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span className="text-2xs uppercase tracking-wide text-neutral-500 font-semibold">
            {t('amazonListing.slots.generatedImages')}
          </span>
          {filledCount > 0 && (
            <span className="bg-neutral-200 dark:bg-neutral-700 px-2 py-0.5 rounded-full text-2xs text-neutral-500">
              {filledCount}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {generations.map((gen, index) => (
            <SlotGenerationThumbnail
              key={gen.id}
              slotId={slotId}
              gen={gen}
              isSelected={gen.id === (selectedId ?? generations[0]?.id)}
              onSelect={onSelect}
              onOpenLightbox={() => setLightboxIndex(index)}
              onDelete={onDelete}
            />
          ))}
          {Array.from({ length: emptyCount }, (_, i) => (
            <div
              key={`empty-${i}`}
              className="w-full aspect-square rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-600 flex items-center justify-center"
            >
              {isGenerating ? (
                <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
              ) : (
                <span className="text-2xs text-neutral-400 text-center px-2">
                  {t('amazonListing.slots.emptySlot')}
                </span>
              )}
            </div>
          ))}
        </div>

        {filledCount > 0 && (
          <button
            onClick={handleDownloadAll}
            className="flex items-center justify-center gap-2 w-full px-3 py-2 text-xs border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            {t('amazonListing.slots.downloadAll')}
          </button>
        )}
      </div>

      <SlotGenerationLightbox
        slotId={slotId}
        generations={generations}
        currentIndex={lightboxIndex ?? 0}
        open={lightboxIndex !== null}
        onOpenChange={(open) => { if (!open) setLightboxIndex(null) }}
        onNavigate={setLightboxIndex}
      />
    </>
  )
}
